import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { login as apiLogin } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Cloud, Loader2, Lock, User } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminLogin() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiLogin(username, password);
      
      if (response.success) {
        if (response.user.role !== 'admin' && response.user.role !== 'manager') {
          toast.error('Keine Berechtigung für Manager-Portal');
          return;
        }
        
        login(response.user, response.token);
        toast.success(`Willkommen, ${response.user.name}!`);
        navigate('/admin/dashboard');
      } else {
        toast.error(response.message || 'Anmeldung fehlgeschlagen');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Fehler bei der Anmeldung');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1a2f38] to-[#2d4a56] p-4">
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-[#1a2f38] rounded-2xl flex items-center justify-center">
            <Cloud className="w-8 h-8 text-green-400" />
          </div>
          <CardTitle className="text-2xl font-bold text-[#1a2f38]">Manager Portal</CardTitle>
          <CardDescription>
            FiscalPOS Cloud Administration
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Benutzername</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="admin"
                  className="h-12 pl-10"
                  data-testid="admin-login-username"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  data-gramm="false"
                  data-gramm_editor="false"
                  autoFocus
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Passwort</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-12 pl-10"
                  data-testid="admin-login-password"
                  autoComplete="current-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  data-gramm="false"
                  data-gramm_editor="false"
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full h-12 text-lg bg-[#1a2f38] hover:bg-[#2a4048]"
              disabled={isLoading}
              data-testid="admin-login-submit"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Anmelden...
                </>
              ) : (
                'Anmelden'
              )}
            </Button>
          </form>
          
          <div className="mt-6 rounded-lg bg-slate-50 p-4">
            <p className="text-sm text-slate-600 text-center">
              Melden Sie sich mit Ihrem vorhandenen Admin- oder Manager-Benutzer an.
            </p>
          </div>
          
          <div className="mt-4 text-center">
            <a 
              href="/pos/login" 
              className="text-sm text-[#1a2f38] hover:underline"
            >
              Zum POS Terminal →
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
